import React, { useState, useEffect, useRef } from 'react';
import utils from 'fantastic/lib/utils';
import SlideredNumberSetting from 'fantastic/components/common/controls/SlideredNumberSetting';
import NumberInput from 'fantastic/components/common/NumberInput';

const BorderRadius = (props) => {
  const { displayInput, min, max, step, onChange, value = '' } = props;
  const { px } = utils;
  const detailsRef = useRef(null);

  const getRadiusArray = (val) => {
    const arr = (val || '')
      .split(' ')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    if (arr.length === 0) return ['0', '0', '0', '0'];
    if (arr.length === 1) return [arr[0], arr[0], arr[0], arr[0]];
    if (arr.length < 4) {
      // Дополняем массив до 4 элементов нулями
      return [...arr, ...Array(4 - arr.length).fill('0')];
    }
    return arr;
  };

  const isAllValuesSame = (val) => {
    const arr = getRadiusArray(val);
    // Проверяем, что массив имеет ровно 4 элемента и все они одинаковые
    if (arr.length !== 4) return false;
    const first = arr[0];
    return arr.every((v) => v === first);
  };

  const [isSingle, setIsSingle] = useState(() => isAllValuesSame(value));
  const [radiusArrState, setRadiusArrState] = useState(() => getRadiusArray(value));
  const prevValueRef = useRef(value);

  useEffect(() => {
    setRadiusArrState(getRadiusArray(value));
    
    // Если значение изменилось извне (не от наших handleCornerChange/handleAllChange)
    // то автоматически определяем режим
    if (prevValueRef.current !== value) {
      setIsSingle(isAllValuesSame(value));
      prevValueRef.current = value;
    }
  }, [value]);



  useEffect(() => {
    if (detailsRef.current) {
      detailsRef.current.open = !isSingle;
    }
  }, [isSingle]);

  const note = () => {
    if (value && isSingle) {
      const first = value.split(' ')[0];
      return px(first);
    }
  };

  const handleCornerChange = (idx) => (newVal) => {
    setRadiusArrState((prevArr) => {
      const arr = [...prevArr];
      arr[idx] = newVal;
      const newValue = arr.join(' ');
      onChange(newValue);
      // Обновляем prevValueRef чтобы useEffect не переключал режим
      prevValueRef.current = newValue;
      return arr;
    });
  };

  const handleAllChange = (newVal) => {
    // При изменении через слайдер сохраняем одно значение
    onChange(newVal);
    // Обновляем prevValueRef чтобы useEffect не переключал режим
    prevValueRef.current = newVal;
  };

  const handleToggle = (e) => {
    e.preventDefault(); // Предотвращаем стандартное поведение details/summary
    
    if (isSingle) {
      // Переключаемся с единого на раздельный режим
      // Копируем текущее единое значение на все 4 угла
      const currentValue = radiusArrState[0] || '10';
      const arr = [currentValue, currentValue, currentValue, currentValue];
      const newValue = arr.join(' ');
      onChange(newValue);
      // Обновляем prevValueRef чтобы useEffect не переключал режим обратно
      prevValueRef.current = newValue;
      setIsSingle(false);
    } else {
      // Переключаемся с раздельного на единый режим
      // Берем первое значение
      const firstValue = radiusArrState[0] || '10';
      onChange(firstValue);
      // Обновляем prevValueRef чтобы useEffect не переключал режим обратно
      prevValueRef.current = firstValue;
      setIsSingle(true);
    }
  };

  const renderBorderInputs = () => (
    <div className="border-inputs">
      <div className="border-inputs__icon"></div>
      <div className="border-inputs__row">
        <div className="border-inputs__input">
          <NumberInput value={radiusArrState[0] || ''} onChange={handleCornerChange(0)} max={max} min={min} />
        </div>
        <div className="border-inputs__input">
          <NumberInput value={radiusArrState[1] || ''} onChange={handleCornerChange(1)} max={max} min={min} />
        </div>
      </div>
      <div className="border-inputs__row">
        <div className="border-inputs__input">
          <NumberInput value={radiusArrState[3] || ''} onChange={handleCornerChange(3)} max={max} min={min} />
        </div>
        <div className="border-inputs__input">
          <NumberInput value={radiusArrState[2] || ''} onChange={handleCornerChange(2)} max={max} min={min} />
        </div>
      </div>
    </div>
  );

  const renderCollapse = () => (
    <details ref={detailsRef} className="ftc-range__collapse">
      <summary className="ftc-range__collapse-btn" type="button" onClick={handleToggle}>
        <span className="ftc-range__collapse-btn-text">
          {isSingle ? i18n.t('border_radius_single') : i18n.t('border_radius_different')}
        </span>
        <div className="ftc-range__collapse-btn-icon"></div>
      </summary>
      <div className="ftc-range__collapse-content">{renderBorderInputs()}</div>
    </details>
  );

  return (
    <div className="ftc-range">
      <div className="ftc-range__range">
        <SlideredNumberSetting
          displayInput={displayInput}
          isDisabled={!isSingle}
          note={note()}
          min={min}
          max={max}
          step={step}
          value={isSingle ? radiusArrState[0] : ''}
          label={i18n.t('border_radius')}
          onChange={handleAllChange}
        />
      </div>
      {renderCollapse()}
    </div>
  );
};

export default BorderRadius;
